/**
 * Phone Number Validation and Formatting Utilities
 *
 * These utilities ensure consistent phone number handling across the application,
 * particularly for Supabase Auth OTP verification which requires E.164 format.
 *
 * E.164 Format: +[country code][subscriber number]
 * Example: +18676896780
 *
 * CRITICAL: Always use normalizeToE164() before sending phone to Supabase Auth
 */

import { parsePhoneNumberFromString } from 'libphonenumber-js';

/**
 * Normalize phone number to E.164 format
 * Ensures phone always has + prefix required by Supabase Auth
 *
 * @param phone - Phone number in any format
 * @returns Phone number in E.164 format with + prefix, or null if invalid
 *
 * @example
 * normalizeToE164('18676896780')   // Returns: '+18676896780'
 * normalizeToE164('+18676896780')  // Returns: '+18676896780'
 * normalizeToE164(null)            // Returns: null
 */
export function normalizeToE164(phone: string | null | undefined): string | null {
  if (!phone) return null;

  // Remove all whitespace and formatting characters
  const cleaned = phone.replace(/[\s\-\(\)\.]/g, '');

  // Already in E.164 format
  if (cleaned.startsWith('+')) {
    return cleaned;
  }

  // Add + prefix to numeric-only phone numbers
  if (/^\d+$/.test(cleaned)) {
    return `+${cleaned}`;
  }

  // Invalid format. The value is never logged: callers pass inbound sender
  // ids and member numbers (no addresses in logs).
  console.warn('[PhoneValidation] Invalid phone format');
  return null;
}

/**
 * Normalize a WhatsApp sender id (`WaId`) to the E.164 we store, dial and link.
 * WhatsApp still addresses many Mexican mobiles with the "1" Mexico dropped in
 * 2019 (`521` + 10 digits), while members are stored as `+52` + 10 digits —
 * without this, a member Tessa already calls falls into the visitor interview.
 *
 * @example
 * normalizeWhatsAppSender('5216671234567')  // Returns: '+526671234567'
 * normalizeWhatsAppSender('14165550100')    // Returns: '+14165550100'
 */
export function normalizeWhatsAppSender(waId: string | null | undefined): string | null {
  const e164 = normalizeToE164(waId);
  if (!e164) return null;
  const legacyMexicanMobile = /^\+521(\d{10})$/.exec(e164);
  return legacyMexicanMobile ? `+52${legacyMexicanMobile[1]}` : e164;
}

/**
 * Validate if phone number is in valid E.164 format
 *
 * @param phone - Phone number to validate
 * @returns true if valid E.164 format, false otherwise
 *
 * @example
 * isValidE164('+18676896780')  // Returns: true
 * isValidE164('18676896780')   // Returns: false (missing +)
 * isValidE164('+1-867-689-6780') // Returns: false (contains formatting)
 */
export function isValidE164(phone: string | null | undefined): boolean {
  if (!phone) return false;

  // E.164 format: + followed by 1-15 digits
  const e164Regex = /^\+\d{1,15}$/;
  return e164Regex.test(phone);
}

/**
 * Mask phone number for display purposes
 * Shows first few and last few digits, masks middle digits
 *
 * @param phone - Phone number to mask (preferably in E.164 format)
 * @returns Masked phone number for safe display
 *
 * @example
 * maskPhone('+18676896780')  // Returns: '+1••••6780'
 * maskPhone('18676896780')   // Returns: '18••••6780'
 */
export function maskPhone(phone: string | null | undefined): string {
  if (!phone) return '';

  // For E.164 format (+1234567890)
  if (phone.startsWith('+')) {
    // Show country code and last 4 digits
    const match = phone.match(/^(\+\d{1,3})\d+(\d{4})$/);
    if (match) {
      return `${match[1]}••••${match[2]}`;
    }
  }

  // For non-E.164 format (1234567890)
  const match = phone.match(/^(\d{1,3})\d+(\d{4})$/);
  if (match) {
    return `${match[1]}••••${match[2]}`;
  }

  // Fallback: just show last 4 digits
  if (phone.length >= 4) {
    return `••••${phone.slice(-4)}`;
  }

  return '••••';
}

/**
 * Compare two phone numbers for equality, accounting for format differences
 *
 * @param phone1 - First phone number
 * @param phone2 - Second phone number
 * @returns true if phones are the same (ignoring formatting)
 *
 * @example
 * arePhoneNumbersEqual('+18676896780', '18676896780')  // Returns: true
 * arePhoneNumbersEqual('+1 (867) 689-6780', '+18676896780')  // Returns: true
 */
export function arePhoneNumbersEqual(
  phone1: string | null | undefined,
  phone2: string | null | undefined
): boolean {
  if (!phone1 || !phone2) return false;

  const normalized1 = normalizeToE164(phone1);
  const normalized2 = normalizeToE164(phone2);

  return normalized1 === normalized2;
}

/**
 * Extract country code from E.164 phone number
 *
 * @param phone - Phone number in E.164 format
 * @returns Country code (e.g., '1' for US/Canada) or null
 *
 * @example
 * getCountryCode('+18676896780')  // Returns: '1'
 * getCountryCode('+442071234567') // Returns: '44'
 */
export function getCountryCode(phone: string | null | undefined): string | null {
  if (!phone || !phone.startsWith('+')) return null;

  // Common country codes (1-3 digits)
  const match = phone.match(/^\+(\d{1,3})/);
  return match ? match[1] : null;
}

/**
 * Log phone format details for debugging
 * Helps track down phone format issues in OTP flow
 *
 * @param context - Description of where this is being called from
 * @param phone - Phone number to log
 * @param additionalInfo - Any additional context
 */
/**
 * Validate an owner-supplied phone into E.164, requiring an EXPLICIT country
 * code. Because these numbers are texted/dialed with no OTP verification, we
 * do NOT let normalizeToE164 guess a country by prefixing '+': a bare
 * national number (e.g. NANP '6046914646') would become a wrong-country
 * international number that passes isValidE164. Accepts ONLY input that
 * already carries a leading '+' and normalizes to valid E.164. Returns the
 * normalized E.164 string, or null.
 *
 * Lives in the pure utils (not a service) so client components can share the
 * exact server rule for inline pre-validation — a looser client regex lets a
 * junk value through to a whole-batch 400 after work is already done.
 */
export function validateOwnerSuppliedPhone(raw: string): string | null {
  if (!raw || typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  // Require an explicit international prefix — no country guessing.
  if (!trimmed.startsWith('+')) return null;
  const normalized = normalizeToE164(trimmed);
  if (!normalized || !isValidE164(normalized)) return null;
  return normalized;
}

/** NANP prefixes that can never be a personal line — metadata-independent
 *  belt-and-braces alongside libphonenumber's getType() (which returns
 *  undefined for many valid numbers and must never be the sole judge). */
const NANP_TOLL_FREE_AREA_CODES = new Set(['800', '833', '844', '855', '866', '877', '888']);
const NANP_PREMIUM_AREA_CODES = new Set(['900']);

/** libphonenumber line types that cannot anchor a workspace (the owner's
 *  verified phone is the abuse stack's identity rung — number policy
 *  2026-07-24: personal numbers only, no toll-free/premium/virtual ranges).
 *  MOBILE / FIXED_LINE / FIXED_LINE_OR_MOBILE / undefined all pass — missing
 *  metadata must never reject a legitimate personal number. */
const DISALLOWED_OWNER_PHONE_TYPES = new Set([
  'TOLL_FREE',
  'PREMIUM_RATE',
  'SHARED_COST',
  'PAGER',
  'UAN',
  'VOICEMAIL',
]);

/**
 * Classify an E.164 owner phone for verification eligibility. Rejecting
 * happens BEFORE the OTP SMS fires (client) and again defensively at sync
 * (server) — a toll-free "owner" identity would anchor workspace-number
 * inference, dial-policy country allowances, and the card-unlock gate to a
 * number nobody personally holds.
 */
export function classifyOwnerPhone(
  e164: string,
): { ok: true } | { ok: false; reason: 'toll_free' | 'premium_rate' | 'non_personal' } {
  const digits = (e164 || '').replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    const area = digits.slice(1, 4);
    if (NANP_TOLL_FREE_AREA_CODES.has(area)) return { ok: false, reason: 'toll_free' };
    if (NANP_PREMIUM_AREA_CODES.has(area)) return { ok: false, reason: 'premium_rate' };
  }
  try {
    const parsed = parsePhoneNumberFromString(e164);
    const type = parsed?.getType();
    if (type && DISALLOWED_OWNER_PHONE_TYPES.has(type)) {
      return {
        ok: false,
        reason: type === 'TOLL_FREE' ? 'toll_free' : type === 'PREMIUM_RATE' ? 'premium_rate' : 'non_personal',
      };
    }
  } catch {
    // Parsing hiccups never reject — format validity is the caller's job.
  }
  return { ok: true };
}

export function logPhoneFormat(
  context: string,
  phone: string | null | undefined,
  additionalInfo?: Record<string, any>
): void {
  if (!phone) {
    console.log(`[PhoneFormat] ${context}: No phone number provided`, additionalInfo);
    return;
  }

  const isE164 = isValidE164(phone);
  const normalized = normalizeToE164(phone);
  const countryCode = getCountryCode(normalized);

  console.log(`[PhoneFormat] ${context}:`, {
    original: phone,
    isE164Valid: isE164,
    normalized: normalized,
    masked: maskPhone(phone),
    countryCode: countryCode,
    ...additionalInfo,
  });
}
